import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, MousePointerClick, MessageCircle, Eye, ArrowUp, ArrowDown, Filter, ChevronDown, Check, AlertCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AccountFinal, PersonFinal } from '@/api/entities';
import { createPageUrl } from '@/utils';
import { sanitizeId } from '@/components/utils/id';
import EngagementShortlistPanel from '@/components/analytics/EngagementShortlistPanel';
import { appendAnalyticsWindow, coerceAnalyticsWindow, windowDayCount } from '@/lib/analyticsWindow';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

/** Must be large enough that longer windows are not missing countries vs shorter windows due to server-side cap + sort. */
const ENGAGEMENT_PERSONAS_LIMIT = 5000;

function getNextScheduledRaw(p) {
  if (!p || typeof p !== 'object') return null;
  const v =
    p.next_scheduled ??
    p.next_scheduled_at ??
    p.next_send_at ??
    p.scheduled_send_at ??
    p.next_touch_scheduled_at ??
    null;
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s || s === 'null') return null;
  return s;
}

function startOfTodayMs() {
  const x = new Date();
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function parseScheduledMs(iso) {
  if (iso === null || iso === undefined) return null;
  const s = String(iso).trim();
  if (!s) return null;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Duplicate persona rows often carry different schedule snapshots. Pick the soonest *upcoming*
 * time (>= local start of today); otherwise the latest timestamp so a stale past row does not
 * wipe the real next send (plain `earliest()` was doing that for 30d merges).
 */
function mergeNextScheduledForPair(rawA, rawB) {
  const items = [];
  for (const raw of [rawA, rawB]) {
    const ms = parseScheduledMs(raw);
    if (ms != null) items.push({ raw, ms });
  }
  if (items.length === 0) return null;
  const t0 = startOfTodayMs();
  const upcoming = items.filter((o) => o.ms >= t0);
  if (upcoming.length > 0) {
    return upcoming.reduce((best, cur) => (cur.ms < best.ms ? cur : best)).raw;
  }
  return items.reduce((best, cur) => (cur.ms > best.ms ? cur : best)).raw;
}

/**
 * One list row per real person. The personas-engagement API can emit multiple rows for the same
 * person_id (e.g. different account_id on different joins, or one row missing account_id) — a
 * person_id+account_id key would keep those as duplicates.
 */
function engagementPersonaDedupeKey(p, fallbackIndex) {
  const pid = sanitizeId(p?.person_id ?? p?.persona_id);
  if (pid) return `pid::${pid}`;
  const email = String(p?.person_email || p?.email || '').trim().toLowerCase();
  if (email) return `em::${email}`;
  const aid = sanitizeId(p?.account_id);
  const aname = String(p?.account_name || '').trim().toLowerCase();
  if (aid && aname) return `acct::${aid}::${aname}`;
  if (aid) return `acct::${aid}::${fallbackIndex}`;
  return `row::${fallbackIndex}`;
}

function mergeEngagementPersonaPair(a, b) {
  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };
  const latest = (x, y) => {
    if (!y) return x ?? y;
    if (!x) return y;
    const tx = new Date(x).getTime();
    const ty = new Date(y).getTime();
    if (!Number.isFinite(tx)) return y;
    if (!Number.isFinite(ty)) return x;
    return tx >= ty ? x : y;
  };
  const touchesResolved = (r) => {
    const toNum = (v) => {
      const x = Number(v);
      return Number.isFinite(x) ? x : 0;
    };
    const ex = r?.touches_count ?? r?.total_touches ?? r?.total_interactions ?? r?.interactions;
    if (ex !== null && ex !== undefined && ex !== '') return toNum(ex);
    return (
      toNum(r?.emails_sent) +
      toNum(r?.linkedin_sent ?? r?.linkedin_messages_sent) +
      toNum(r?.call_sent ?? r?.calls_sent)
    );
  };
  const li = (r) =>
    r?.linkedin_connected === true || r?.linkedin_connected === 1 || r?.linkedin_connected === 'true';
  const out = { ...a };
  out.human_opens = Math.max(n(a.human_opens), n(b.human_opens));
  out.human_clicks = Math.max(n(a.human_clicks), n(b.human_clicks));
  out.replies = Math.max(n(a.replies), n(b.replies));
  out.last_human_open_at = latest(a.last_human_open_at, b.last_human_open_at);
  out.last_human_click_at = latest(a.last_human_click_at, b.last_human_click_at);
  out.last_touch_at = latest(a.last_touch_at, b.last_touch_at);
  out.last_message_sent_at = latest(a.last_message_sent_at, b.last_message_sent_at);
  out.last_sent_at = latest(a.last_sent_at, b.last_sent_at);
  out.last_interaction_at = latest(a.last_interaction_at, b.last_interaction_at);
  out.next_scheduled = mergeNextScheduledForPair(getNextScheduledRaw(a), getNextScheduledRaw(b));
  out.linkedin_connected = li(a) || li(b);
  out.touches_count = Math.max(touchesResolved(a), touchesResolved(b));
  out.emails_sent = Math.max(n(a.emails_sent), n(b.emails_sent));
  out.linkedin_sent = Math.max(
    n(a.linkedin_sent ?? a.linkedin_messages_sent),
    n(b.linkedin_sent ?? b.linkedin_messages_sent)
  );
  out.linkedin_messages_sent = Math.max(n(a.linkedin_messages_sent), n(b.linkedin_messages_sent));
  out.call_sent = Math.max(n(a.call_sent ?? a.calls_sent), n(b.call_sent ?? b.calls_sent));

  const engagementScore = (r) => n(r.human_clicks) * 10000 + n(r.human_opens);
  const winner = engagementScore(b) > engagementScore(a) ? b : a;
  const loser = winner === b ? a : b;
  out.account_id = sanitizeId(winner.account_id) || sanitizeId(loser.account_id) || sanitizeId(out.account_id);
  out.account_name =
    String(winner.account_name || '').trim() ||
    String(loser.account_name || '').trim() ||
    String(out.account_name || '').trim();
  out.archetype_name = pickFirstNonEmpty(
    a.archetype_name,
    a.archetype,
    b.archetype_name,
    b.archetype,
    out.archetype_name,
    out.archetype
  );
  out.department = pickFirstNonEmpty(a.department, b.department, out.department);

  return out;
}

function dedupeEngagementPersonas(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const map = new Map();
  rows.forEach((p, i) => {
    const k = engagementPersonaDedupeKey(p, i);
    if (map.has(k)) map.set(k, mergeEngagementPersonaPair(map.get(k), p));
    else map.set(k, p);
  });
  return Array.from(map.values());
}

function formatNextScheduled(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'MMM d, h:mm a');
}

function formatRel(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

function isSameLocalDay(iso, now = new Date()) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function startOfLocalDay(d = new Date()) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

/** 0 = last touch on local calendar today, 1 = yesterday, … */
function calendarDaysFromLastTouchToToday(iso) {
  if (!iso) return null;
  const touch = new Date(iso);
  if (Number.isNaN(touch.getTime())) return null;
  const touchDay = startOfLocalDay(touch);
  const today = startOfLocalDay(new Date());
  return Math.round((today - touchDay) / 86400000);
}

function matchesLastTouchFilter(iso, filterKey) {
  if (!filterKey || filterKey === 'all') return true;
  if (!iso) return false;
  const d = calendarDaysFromLastTouchToToday(iso);
  if (d === null || d < 0) return false;
  if (filterKey === 'today') return d === 0;
  if (filterKey === 'yesterday') return d === 1;
  if (filterKey === 'days_2') return d === 2;
  if (filterKey === 'days_3') return d === 3;
  if (filterKey === 'days_4') return d === 4;
  if (filterKey === 'days_5') return d === 5;
  if (filterKey === 'days_6') return d === 6;
  if (filterKey === 'days_7_plus') return d >= 7;
  return true;
}

/** Same calendar-day buckets as the Last touch filter (local midnight boundaries). */
function formatLastTouchCalendarLabel(iso) {
  if (!iso) return '—';
  const d = calendarDaysFromLastTouchToToday(iso);
  if (d === null) return '—';
  if (d < 0) return 'Future';
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d >= 2 && d <= 6) return `${d} days ago`;
  return '7+ days ago';
}

function formatLastTouchTooltip(iso) {
  if (!iso) return 'No last touch timestamp';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return format(d, 'MMM d, yyyy h:mm a');
}

/** `personas.department` only (personas table); engagement API rows expose it flat on each row. */
function getPersonaTableDepartment(p) {
  if (!p || typeof p !== 'object') return '';
  return String(p.department ?? '').trim();
}

function pickFirstNonEmpty(...values) {
  for (const v of values) {
    const s = String(v ?? '').trim();
    if (s) return s;
  }
  return '';
}

/** API rows may use archetype_name or archetype; dedupe must keep whichever is present. */
function getPersonaArchetypeName(p) {
  if (!p || typeof p !== 'object') return '';
  return pickFirstNonEmpty(p.archetype_name, p.archetype, p.persona_archetype);
}

function getPersonProfileUrl(p) {
  const personId = sanitizeId(p?.person_id || p?.id || p?.persona_id);
  if (!personId) return null;
  const accountId = sanitizeId(p?.account_id);
  const path = accountId
    ? `PersonProfile?id=${encodeURIComponent(personId)}&accountId=${encodeURIComponent(accountId)}`
    : `PersonProfile?id=${encodeURIComponent(personId)}`;
  return createPageUrl(path);
}

function getAccountProfileUrl(p, accountIdByNameMap) {
  let accountId = sanitizeId(p?.account_id);
  if (!accountId && accountIdByNameMap) {
    const name = String(p?.account_name || '').trim().toLowerCase();
    if (name) accountId = sanitizeId(accountIdByNameMap.get(name));
  }
  if (!accountId) return null;
  return createPageUrl(`AccountFinal?id=${encodeURIComponent(accountId)}`);
}

function getAccountFitScore(row) {
  const candidates = [
    row?.account_fit_score,
    row?.account_fit,
    row?.account_score,
    row?.fit_score_account,
    row?.account?.account_fit_score,
    row?.account?.account_fit,
  ];
  for (const value of candidates) {
    if (value === null || value === undefined || value === '') continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function formatAccountFitIntegerDisplay(score) {
  if (score == null || score === '') return '—';
  const n = Number(score);
  if (!Number.isFinite(n)) return '—';
  const v = n > 0 && n <= 1 ? Math.round(n * 100) : Math.round(n);
  return String(v);
}

const ACCOUNT_TIER_OPTIONS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'];

function normalizeAccountTier(tier) {
  if (tier == null || tier === '') return null;
  const raw = String(tier).trim();
  if (!raw) return null;
  const tierMatch = raw.match(/Tier\s*(\d)/i);
  if (tierMatch) return `Tier ${tierMatch[1]}`;
  const tMatch = raw.match(/^T(\d)$/i);
  if (tMatch) return `Tier ${tMatch[1]}`;
  if (/^\d$/.test(raw)) return `Tier ${raw}`;
  return raw;
}

function deriveAccountTierFromFitScore(score, decisionWindowBand) {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  const fit = n > 0 && n <= 1 ? n * 100 : n;
  const isAplus = fit >= 90 && decisionWindowBand === '<12m';
  if (isAplus || fit >= 90) return 'Tier 1';
  if (fit >= 75) return 'Tier 2';
  if (fit >= 60) return 'Tier 3';
  return 'Tier 4';
}

function resolveAccountTierFromRecord(acc) {
  const explicit = normalizeAccountTier(acc?.account_tier ?? acc?.tier);
  if (explicit) return explicit;
  return deriveAccountTierFromFitScore(
    acc?.account_fit_score ?? acc?.account_fit ?? acc?.fit_score,
    acc?.decision_window_band,
  );
}

function collectHiddenPersonIds(rows) {
  const set = new Set();
  for (const row of rows || []) {
    for (const key of ['person_id', 'id', 'persona_id']) {
      const id = sanitizeId(row?.[key]);
      if (id) set.add(id);
    }
  }
  return set;
}

function isEngagementPersonaVisible(p, { hiddenAccountIds, hiddenAccountNames, hiddenPersonaIds }) {
  if (p?.is_visible === false) return false;
  const personId = sanitizeId(p?.person_id ?? p?.persona_id);
  if (personId && hiddenPersonaIds.has(personId)) return false;
  const accountId = String(p?.account_id || '').trim();
  if (accountId && hiddenAccountIds.has(accountId)) return false;
  const accountName = String(p?.account_name || '').trim().toLowerCase();
  if (accountName && hiddenAccountNames.has(accountName)) return false;
  return true;
}

function appendEngagementVisibilityParams(params) {
  params.append('only_visible_accounts', 'true');
  params.append('only_visible_personas', 'true');
}

export default function EngagementTab({ tenantId, timeRange, analyticsWindow, userId }) {
  const [engagedData, setEngagedData] = useState(null);
  const [untouchedData, setUntouchedData] = useState(null);
  const [engagedLoading, setEngagedLoading] = useState(true);
  const [untouchedLoading, setUntouchedLoading] = useState(true);
  const [cohort, setCohort] = useState('engaged');
  const [archetypeFilter, setArchetypeFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'human_clicks', direction: 'desc' });
  const [countryFilter, setCountryFilter] = useState(new Set());
  const [accountFilter, setAccountFilter] = useState(new Set());
  const [departmentFilter, setDepartmentFilter] = useState(new Set());
  const [typeFilter, setTypeFilter] = useState(new Set());
  const [accountSearch, setAccountSearch] = useState('');
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [lastTouchFilter, setLastTouchFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState(new Set());
  const [accountCountryMap, setAccountCountryMap] = useState(new Map());
  const [accountCountryByIdMap, setAccountCountryByIdMap] = useState(new Map());
  const [accountFitByNameMap, setAccountFitByNameMap] = useState(new Map());
  const [accountFitByIdMap, setAccountFitByIdMap] = useState(new Map());
  const [accountTierByNameMap, setAccountTierByNameMap] = useState(new Map());
  const [accountTierByIdMap, setAccountTierByIdMap] = useState(new Map());
  const [accountSectorByNameMap, setAccountSectorByNameMap] = useState(new Map());
  const [accountSectorByIdMap, setAccountSectorByIdMap] = useState(new Map());
  const [accountIdByNameMap, setAccountIdByNameMap] = useState(new Map());
  const [accountTypeOptions, setAccountTypeOptions] = useState([]);
  const [hiddenAccountIds, setHiddenAccountIds] = useState(new Set());
  const [hiddenAccountNames, setHiddenAccountNames] = useState(new Set());
  const [hiddenPersonaIds, setHiddenPersonaIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    const loadAccountCountries = async () => {
      try {
        const [rows, hiddenPeopleRows] = await Promise.all([
          AccountFinal.list(),
          PersonFinal.filter({ tenant_id: tenantId, is_visible: false }, '-updated_at', 10000).catch(() => []),
        ]);
        if (cancelled || !Array.isArray(rows)) return;
        const countryMap = new Map();
        const countryByIdMap = new Map();
        const fitByNameMap = new Map();
        const fitByIdMap = new Map();
        const tierByNameMap = new Map();
        const tierByIdMap = new Map();
        const sectorByNameMap = new Map();
        const sectorByIdMap = new Map();
        const accountIdByName = new Map();
        const sectorOptionsSet = new Set();
        const nextHiddenAccountIds = new Set();
        const nextHiddenAccountNames = new Set();
        rows.forEach((acc) => {
          if (acc?.tenant_id !== tenantId && acc?.tenant_id !== 'global') return;
          const name = String(acc?.account_name || '').trim().toLowerCase();
          const accountId = String(acc?.account_id || acc?.id || '').trim();
          if (acc?.is_visible === false) {
            if (accountId) nextHiddenAccountIds.add(accountId);
            if (name) nextHiddenAccountNames.add(name);
            return;
          }
          const country = String(acc?.country || acc?.country_name || '').trim();
          if (name && country && !countryMap.has(name)) countryMap.set(name, country);
          if (accountId && country && !countryByIdMap.has(accountId)) countryByIdMap.set(accountId, country);
          const fitRaw = acc?.account_fit_score ?? acc?.account_fit ?? acc?.fit_score ?? null;
          const fitNum = fitRaw === null || fitRaw === undefined || fitRaw === '' ? null : Number(fitRaw);
          if (Number.isFinite(fitNum)) {
            if (name && !fitByNameMap.has(name)) fitByNameMap.set(name, fitNum);
            if (accountId && !fitByIdMap.has(accountId)) fitByIdMap.set(accountId, fitNum);
          }
          const tier = resolveAccountTierFromRecord(acc);
          if (tier) {
            if (name && !tierByNameMap.has(name)) tierByNameMap.set(name, tier);
            if (accountId && !tierByIdMap.has(accountId)) tierByIdMap.set(accountId, tier);
          }
          const sector = String(acc?.sector || '').trim();
          if (sector) {
            sectorOptionsSet.add(sector);
            if (name && !sectorByNameMap.has(name)) sectorByNameMap.set(name, sector);
            if (accountId && !sectorByIdMap.has(accountId)) sectorByIdMap.set(accountId, sector);
          }
          if (name && accountId && !accountIdByName.has(name)) accountIdByName.set(name, accountId);
        });
        setAccountCountryMap(countryMap);
        setAccountCountryByIdMap(countryByIdMap);
        setAccountFitByNameMap(fitByNameMap);
        setAccountFitByIdMap(fitByIdMap);
        setAccountTierByNameMap(tierByNameMap);
        setAccountTierByIdMap(tierByIdMap);
        setAccountSectorByNameMap(sectorByNameMap);
        setAccountSectorByIdMap(sectorByIdMap);
        setAccountIdByNameMap(accountIdByName);
        setAccountTypeOptions(Array.from(sectorOptionsSet).sort((a, b) => a.localeCompare(b)));
        setHiddenAccountIds(nextHiddenAccountIds);
        setHiddenAccountNames(nextHiddenAccountNames);
        setHiddenPersonaIds(collectHiddenPersonIds(hiddenPeopleRows));
      } catch {
        setAccountCountryMap(new Map());
        setAccountCountryByIdMap(new Map());
        setAccountFitByNameMap(new Map());
        setAccountFitByIdMap(new Map());
        setAccountTierByNameMap(new Map());
        setAccountTierByIdMap(new Map());
        setAccountSectorByNameMap(new Map());
        setAccountSectorByIdMap(new Map());
        setAccountIdByNameMap(new Map());
        setAccountTypeOptions([]);
        setHiddenAccountIds(new Set());
        setHiddenAccountNames(new Set());
        setHiddenPersonaIds(new Set());
      }
    };
    if (tenantId) loadAccountCountries();
    return () => { cancelled = true; };
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setEngagedLoading(true);
    setUntouchedLoading(true);

    const engagedParams = new URLSearchParams({
      tenant_id: tenantId,
      days: String(timeRange || 30),
      min_opens: '1',
      min_clicks: '0',
      limit: String(ENGAGEMENT_PERSONAS_LIMIT),
    });
    appendAnalyticsWindow(engagedParams, coerceAnalyticsWindow(analyticsWindow ?? timeRange));
    if (userId) engagedParams.append('user_id', userId);
    appendEngagementVisibilityParams(engagedParams);

    const untouchedParams = new URLSearchParams({ tenant_id: tenantId });
    if (userId) untouchedParams.append('user_id', userId);
    appendEngagementVisibilityParams(untouchedParams);

    const fetchOpts = {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    };

    Promise.all([
      fetch(`${API_BASE_URL}/api/email-analytics/personas-engagement?${engagedParams.toString()}`, fetchOpts)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
      fetch(`${API_BASE_URL}/api/email-analytics/untouched-personas?${untouchedParams.toString()}`, fetchOpts)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ]).then(([engaged, untouched]) => {
      if (cancelled) return;
      setEngagedData(engaged);
      setUntouchedData(untouched);
    }).finally(() => {
      if (!cancelled) {
        setEngagedLoading(false);
        setUntouchedLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [tenantId, timeRange, analyticsWindow, userId]);

  useEffect(() => {
    if (cohort === 'engaged' || cohort === 'untouched') {
      setArchetypeFilter('all');
      setTierFilter(new Set());
      setTypeFilter(new Set());
    }
  }, [cohort]);

  useEffect(() => {
    if (cohort === 'untouched') {
      setLastTouchFilter('all');
    }
  }, [cohort]);

  useEffect(() => {
    setCountryFilter(new Set());
    setAccountFilter(new Set());
    setDepartmentFilter(new Set());
    setTypeFilter(new Set());
    setTierFilter(new Set());
    setAccountSearch('');
    setDepartmentSearch('');
  }, [timeRange, analyticsWindow]);

  const visibilityScope = useMemo(
    () => ({ hiddenAccountIds, hiddenAccountNames, hiddenPersonaIds }),
    [hiddenAccountIds, hiddenAccountNames, hiddenPersonaIds],
  );

  const buildPersonasFromPayload = (payload, { sortEngaged }) => {
    const rows = Array.isArray(payload?.personas) ? payload.personas : [];
    const visibleRows = rows.filter((p) => isEngagementPersonaVisible(p, visibilityScope));
    const deduped = dedupeEngagementPersonas(visibleRows);
    if (!sortEngaged) return deduped;
    return deduped.sort((a, b) => {
      const clickDiff = (b?.human_clicks || 0) - (a?.human_clicks || 0);
      if (clickDiff !== 0) return clickDiff;
      const openDiff = (b?.human_opens || 0) - (a?.human_opens || 0);
      if (openDiff !== 0) return openDiff;
      return (b?.replies || 0) - (a?.replies || 0);
    });
  };

  const engagedPersonas = useMemo(
    () => buildPersonasFromPayload(engagedData, { sortEngaged: true }),
    [engagedData, visibilityScope],
  );

  const untouchedPersonas = useMemo(
    () => buildPersonasFromPayload(untouchedData, { sortEngaged: false }),
    [untouchedData, visibilityScope],
  );

  const combinedPersonas = useMemo(
    () => dedupeEngagementPersonas([...engagedPersonas, ...untouchedPersonas]),
    [engagedPersonas, untouchedPersonas],
  );

  const data = cohort === 'engaged' ? engagedData : untouchedData;
  const personas = cohort === 'engaged'
    ? engagedPersonas
    : cohort === 'untouched'
      ? untouchedPersonas
      : combinedPersonas;
  const loading = cohort === 'engaged'
    ? engagedLoading
    : cohort === 'untouched'
      ? untouchedLoading
      : false;
  const shortlistPoolLoading = engagedLoading || untouchedLoading;

  const archetypeOptions = useMemo(() => {
    const set = new Set();
    personas.forEach((p) => {
      const name = getPersonaArchetypeName(p);
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [personas]);

  const getRowCountry = (p) => {
    const fromAccountFields = String(
      p?.account_country ||
      p?.account_country_name ||
      ''
    ).trim();
    if (fromAccountFields) return fromAccountFields;

    const accountId = String(p?.account_id || '').trim();
    if (accountId && accountCountryByIdMap.has(accountId)) {
      return accountCountryByIdMap.get(accountId);
    }

    const accountName = String(p?.account_name || '').trim().toLowerCase();
    const fromNameMap = accountName ? accountCountryMap.get(accountName) || '' : '';
    if (fromNameMap) return fromNameMap;

    return String(p?.country || p?.country_name || '').trim();
  };

  const countryOptions = useMemo(() => {
    const set = new Set();
    personas.forEach((p) => {
      const c = getRowCountry(p);
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [personas, accountCountryMap, accountCountryByIdMap]);

  const accountOptions = useMemo(() => {
    const set = new Set();
    personas.forEach((p) => {
      const name = String(p?.account_name || '').trim();
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [personas]);

  const filteredAccountOptions = useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    if (!q) return accountOptions;
    return accountOptions.filter((name) => String(name || '').toLowerCase().includes(q));
  }, [accountOptions, accountSearch]);

  const departmentOptions = useMemo(() => {
    const set = new Set();
    personas.forEach((p) => {
      const d = getPersonaTableDepartment(p);
      if (d) set.add(d);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [personas]);

  const filteredDepartmentOptions = useMemo(() => {
    const q = departmentSearch.trim().toLowerCase();
    if (!q) return departmentOptions;
    return departmentOptions.filter((name) => String(name || '').toLowerCase().includes(q));
  }, [departmentOptions, departmentSearch]);

  function getAccountFitScore(p) {
    const direct = p?.account_fit_score;
    if (direct !== null && direct !== undefined && direct !== '') {
      const n = Number(direct);
      if (Number.isFinite(n)) return n;
    }
    const accountId = String(p?.account_id || p?.id || '').trim();
    if (accountId && accountFitByIdMap.has(accountId)) {
      return accountFitByIdMap.get(accountId);
    }
    const accountName = String(p?.account_name || '').trim().toLowerCase();
    return accountFitByNameMap.get(accountName) ?? null;
  }

  const getRowAccountTier = (p) => {
    const fromRow = normalizeAccountTier(p?.account_tier ?? p?.tier ?? p?.account?.account_tier ?? p?.account?.tier);
    if (fromRow) return fromRow;

    const accountId = String(p?.account_id || '').trim();
    if (accountId && accountTierByIdMap.has(accountId)) {
      return accountTierByIdMap.get(accountId);
    }

    const accountName = String(p?.account_name || '').trim().toLowerCase();
    if (accountName && accountTierByNameMap.has(accountName)) {
      return accountTierByNameMap.get(accountName);
    }

    return deriveAccountTierFromFitScore(
      getAccountFitScore(p),
      p?.decision_window_band ?? p?.account_decision_window_band,
    );
  };

  const getRowAccountSector = (p) => {
    const fromRow = String(
      p?.sector ?? p?.account_sector ?? p?.account_type ?? p?.account?.sector ?? '',
    ).trim();
    if (fromRow) return fromRow;

    const accountId = String(p?.account_id || '').trim();
    if (accountId && accountSectorByIdMap.has(accountId)) {
      return accountSectorByIdMap.get(accountId);
    }

    const accountName = String(p?.account_name || '').trim().toLowerCase();
    if (accountName && accountSectorByNameMap.has(accountName)) {
      return accountSectorByNameMap.get(accountName);
    }

    return null;
  };

  const typeOptions = useMemo(() => {
    const set = new Set(accountTypeOptions);
    personas.forEach((p) => {
      const sector = getRowAccountSector(p);
      if (sector) set.add(sector);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [accountTypeOptions, personas, accountSectorByNameMap, accountSectorByIdMap]);

  const getLastActivityAt = (p) => {
    const openTs = p?.last_human_open_at ? new Date(p.last_human_open_at).getTime() : 0;
    const clickTs = p?.last_human_click_at ? new Date(p.last_human_click_at).getTime() : 0;
    if (!openTs && !clickTs) return null;
    return openTs >= clickTs ? p?.last_human_open_at : p?.last_human_click_at;
  };

  const getLastTouchAt = (p) => (
    p?.last_touch_at ||
    p?.last_message_sent_at ||
    p?.last_sent_at ||
    p?.last_interaction_at ||
    getLastActivityAt(p)
  );

  const sortedPersonas = useMemo(() => {
    let rows = [...personas];
    if (countryFilter.size > 0) {
      rows = rows.filter((p) => countryFilter.has(getRowCountry(p)));
    }
    if (accountFilter.size > 0) {
      rows = rows.filter((p) => {
        const name = String(p?.account_name || '').trim();
        return name && accountFilter.has(name);
      });
    }
    if (departmentFilter.size > 0) {
      rows = rows.filter((p) => {
        const d = getPersonaTableDepartment(p);
        return d && departmentFilter.has(d);
      });
    }
    if (lastTouchFilter !== 'all') {
      rows = rows.filter((p) => matchesLastTouchFilter(getLastTouchAt(p), lastTouchFilter));
    }
    if (archetypeFilter !== 'all') {
      rows = rows.filter((p) => getPersonaArchetypeName(p) === archetypeFilter);
    }
    if (tierFilter.size > 0) {
      rows = rows.filter((p) => {
        const tier = getRowAccountTier(p);
        return tier && tierFilter.has(tier);
      });
    }
    if (typeFilter.size > 0) {
      rows = rows.filter((p) => {
        const sector = getRowAccountSector(p);
        return sector && typeFilter.has(sector);
      });
    }
    const { key, direction } = sortConfig;
    const resolveTouches = (p) => {
      const toNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };
      const explicit =
        p?.touches_count ??
        p?.total_touches ??
        p?.total_interactions ??
        p?.interactions ??
        null;
      if (explicit !== null && explicit !== undefined && explicit !== '') return toNum(explicit);
      const email = toNum(p?.emails_sent);
      const linkedin = toNum(p?.linkedin_sent ?? p?.linkedin_messages_sent);
      const calls = toNum(p?.call_sent ?? p?.calls_sent);
      return email + linkedin + calls;
    };
    rows.sort((a, b) => {
      let av;
      let bv;
      if (key === 'last_human_open_at' || key === 'last_human_click_at') {
        av = a?.[key] ? new Date(a[key]).getTime() : 0;
        bv = b?.[key] ? new Date(b[key]).getTime() : 0;
      } else if (key === 'last_activity_at') {
        const aOpen = a?.last_human_open_at ? new Date(a.last_human_open_at).getTime() : 0;
        const aClick = a?.last_human_click_at ? new Date(a.last_human_click_at).getTime() : 0;
        const bOpen = b?.last_human_open_at ? new Date(b.last_human_open_at).getTime() : 0;
        const bClick = b?.last_human_click_at ? new Date(b.last_human_click_at).getTime() : 0;
        av = Math.max(aOpen, aClick);
        bv = Math.max(bOpen, bClick);
      } else if (key === 'last_touch_at') {
        const aTouch = getLastTouchAt(a);
        const bTouch = getLastTouchAt(b);
        av = aTouch ? new Date(aTouch).getTime() : 0;
        bv = bTouch ? new Date(bTouch).getTime() : 0;
      } else if (key === 'next_scheduled') {
        const parse = (iso) => {
          if (!iso) return null;
          const t = new Date(iso).getTime();
          return Number.isNaN(t) ? null : t;
        };
        const aTime = parse(getNextScheduledRaw(a));
        const bTime = parse(getNextScheduledRaw(b));
        // nulls last in both sort directions
        av = aTime === null ? (direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER) : aTime;
        bv = bTime === null ? (direction === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER) : bTime;
      } else if (key === 'linkedin_connected') {
        const isY = (p) =>
          p?.linkedin_connected === true ||
          p?.linkedin_connected === 1 ||
          p?.linkedin_connected === 'true';
        // Y first; N / false sorts last in both directions
        av = isY(a) ? (direction === 'asc' ? 0 : 1) : direction === 'asc' ? 1 : 0;
        bv = isY(b) ? (direction === 'asc' ? 0 : 1) : direction === 'asc' ? 1 : 0;
      } else if (key === 'person_name') {
        av = String(a?.person_name || a?.person_email || '').toLowerCase();
        bv = String(b?.person_name || b?.person_email || '').toLowerCase();
      } else if (key === 'account_name') {
        av = String(a?.account_name || '').toLowerCase();
        bv = String(b?.account_name || '').toLowerCase();
      } else if (key === 'archetype_name') {
        av = getPersonaArchetypeName(a).toLowerCase();
        bv = getPersonaArchetypeName(b).toLowerCase();
      } else if (key === 'account_fit_score') {
        av = getAccountFitScore(a) ?? -1;
        bv = getAccountFitScore(b) ?? -1;
      } else if (key === 'touches_count') {
        av = resolveTouches(a);
        bv = resolveTouches(b);
      } else {
        av = Number(a?.[key] || 0);
        bv = Number(b?.[key] || 0);
      }
      if (av < bv) return direction === 'asc' ? -1 : 1;
      if (av > bv) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [
    personas,
    sortConfig,
    countryFilter,
    accountFilter,
    departmentFilter,
    lastTouchFilter,
    accountCountryMap,
    accountCountryByIdMap,
    accountFitByNameMap,
    accountFitByIdMap,
    accountTierByNameMap,
    accountTierByIdMap,
    accountSectorByNameMap,
    accountSectorByIdMap,
    archetypeFilter,
    tierFilter,
    typeFilter,
  ]);

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc'
      ? <ArrowUp className="w-3 h-3" />
      : <ArrowDown className="w-3 h-3" />;
  };

  const selectedWindowDays = windowDayCount(analyticsWindow ?? timeRange);
  const engagementWindowMismatch =
    cohort === 'engaged' &&
    data?.window_days != null &&
    Number(data.window_days) !== Number(selectedWindowDays);

  const engagementLikelyTruncated =
    cohort === 'engaged' && personas.length >= ENGAGEMENT_PERSONAS_LIMIT;

  const untouchedTotal =
    cohort === 'untouched' && data?.total_personas != null
      ? Number(data.total_personas)
      : cohort === 'untouched'
        ? personas.length
        : null;

  if (cohort !== 'shortlist' && loading) {
    return (
      <Card>
        <CardContent className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  const showArchetypeEmptyRow =
    personas.length > 0 && sortedPersonas.length === 0 && archetypeFilter !== 'all';
  const showUntouchedZeroRows = cohort === 'untouched' && personas.length === 0;
  const showEngagedZeroRows = cohort === 'engaged' && personas.length === 0;
  const showGenericFilterEmptyRow =
    personas.length > 0 && sortedPersonas.length === 0 && archetypeFilter === 'all';

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div
                className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 self-start"
                role="group"
                aria-label="Persona cohort"
              >
                {[
                  { id: 'engaged', label: 'Engaged' },
                  { id: 'shortlist', label: 'Shortlist' },
                  { id: 'untouched', label: 'Untouched' },
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    type="button"
                    className={`rounded-md px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors ${
                      cohort === id ? 'bg-white text-[#0A1A2F] shadow-sm' : 'text-gray-600 hover:text-[#0A1A2F]'
                    }`}
                    onClick={() => setCohort(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {cohort !== 'shortlist' ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-600 shrink-0">Archetype:</span>
                <Select value={archetypeFilter} onValueChange={setArchetypeFilter}>
                  <SelectTrigger className="w-full sm:w-[200px] h-9 text-sm">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {archetypeOptions.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              ) : null}
            </div>
            {cohort === 'shortlist' ? (
              <p className="text-sm text-gray-600">
                Saved views with custom filters. Personas are drawn from both engaged and untouched lists.
              </p>
            ) : (
            <>
            <div className="text-sm text-gray-600">
              {cohort === 'engaged' ? (
                <>
                  <strong>{sortedPersonas.length}</strong> personas with real engagement in the last{' '}
                  {data?.window_days != null ? data.window_days : selectedWindowDays} days
                  {engagementWindowMismatch ? (
                    <span className="ml-2 text-amber-700 font-medium">
                      (selector: {selectedWindowDays}d — API reported {data.window_days}d; filters and counts follow the API window.)
                    </span>
                  ) : null}
                  .
                </>
              ) : (
                <>
                  <strong>{sortedPersonas.length}</strong> of{' '}
                  <strong>{untouchedTotal != null ? untouchedTotal : personas.length}</strong> untouched personas
                  (no outbound touch yet). Sorted with next scheduled first, then by persona fit.
                </>
              )}
            </div>
            {engagementLikelyTruncated && (
              <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  This list hit the maximum fetch size ({ENGAGEMENT_PERSONAS_LIMIT.toLocaleString()} personas). Country,
                  account tier, account, and persona department (<code className="text-[11px] bg-amber-100/80 px-1 rounded">personas.department</code>)
                  filters only reflect loaded rows, so longer windows can look inconsistent vs shorter ones. If this appears
                  often, raise the cap or add paging on the personas-engagement API.
                </span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:justify-end">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-[220px] h-9 text-sm justify-start">
                    <Filter className="w-4 h-4 mr-2 shrink-0" />
                    {tierFilter.size === 0
                      ? 'All account tiers'
                      : tierFilter.size === 1
                        ? Array.from(tierFilter)[0]
                        : `${tierFilter.size} tiers selected`}
                    <ChevronDown className="w-4 h-4 ml-auto opacity-60 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[240px] p-2" align="end">
                  <div className="space-y-2">
                    <div className="font-semibold text-sm px-2">Filter account tiers</div>
                    <div className="max-h-56 overflow-y-auto">
                      {ACCOUNT_TIER_OPTIONS.map((tier) => {
                        const checked = tierFilter.has(tier);
                        return (
                          <label key={tier} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(nextChecked) => {
                                setTierFilter((prev) => {
                                  const next = new Set(prev);
                                  if (nextChecked) next.add(tier);
                                  else next.delete(tier);
                                  return next;
                                });
                              }}
                            />
                            <span className="text-sm">{tier}</span>
                          </label>
                        );
                      })}
                    </div>
                    {tierFilter.size > 0 && (
                      <div className="border-t pt-2">
                        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setTierFilter(new Set())}>
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-[220px] h-9 text-sm justify-start">
                    <Filter className="w-4 h-4 mr-2 shrink-0" />
                    {typeFilter.size === 0
                      ? 'All types'
                      : typeFilter.size === 1
                        ? Array.from(typeFilter)[0]
                        : `${typeFilter.size} types selected`}
                    <ChevronDown className="w-4 h-4 ml-auto opacity-60 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-2" align="end">
                  <div className="space-y-2">
                    <div className="font-semibold text-sm px-2">Filter types</div>
                    <div className="max-h-56 overflow-y-auto">
                      {typeOptions.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                          No account types in this view.
                        </div>
                      ) : (
                        typeOptions.map((sector) => {
                          const checked = typeFilter.has(sector);
                          return (
                            <label key={sector} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(nextChecked) => {
                                  setTypeFilter((prev) => {
                                    const next = new Set(prev);
                                    if (nextChecked) next.add(sector);
                                    else next.delete(sector);
                                    return next;
                                  });
                                }}
                              />
                              <span className="text-sm truncate">{sector}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {typeFilter.size > 0 && (
                      <div className="border-t pt-2">
                        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setTypeFilter(new Set())}>
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Select
                value={lastTouchFilter}
                onValueChange={setLastTouchFilter}
                disabled={cohort === 'untouched'}
              >
                <SelectTrigger className="w-full sm:w-[220px] h-9 text-sm">
                  <Filter className="w-4 h-4 mr-2 shrink-0" />
                  <SelectValue placeholder="Last touch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any last touch</SelectItem>
                  <SelectItem value="today">Last touch: today</SelectItem>
                  <SelectItem value="yesterday">Last touch: yesterday</SelectItem>
                  <SelectItem value="days_2">Last touch: 2 days ago</SelectItem>
                  <SelectItem value="days_3">Last touch: 3 days ago</SelectItem>
                  <SelectItem value="days_4">Last touch: 4 days ago</SelectItem>
                  <SelectItem value="days_5">Last touch: 5 days ago</SelectItem>
                  <SelectItem value="days_6">Last touch: 6 days ago</SelectItem>
                  <SelectItem value="days_7_plus">Last touch: 7+ days ago</SelectItem>
                </SelectContent>
              </Select>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-[220px] h-9 text-sm justify-start">
                    <Filter className="w-4 h-4 mr-2 shrink-0" />
                    {accountFilter.size === 0 ? 'All accounts' : `${accountFilter.size} accounts selected`}
                    <ChevronDown className="w-4 h-4 ml-auto opacity-60 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-2" align="end">
                  <div className="space-y-2">
                    <div className="font-semibold text-sm px-2">Filter accounts</div>
                    <Input
                      type="search"
                      value={accountSearch}
                      onChange={(e) => setAccountSearch(e.target.value)}
                      placeholder="Search account name…"
                      className="h-8 text-sm"
                      autoComplete="off"
                    />
                    <div className="max-h-56 overflow-y-auto">
                      {filteredAccountOptions.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                          {accountOptions.length === 0 ? 'No accounts in this view.' : 'No accounts match your search.'}
                        </div>
                      ) : (
                        filteredAccountOptions.map((accountName) => {
                          const checked = accountFilter.has(accountName);
                          return (
                            <label key={accountName} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(nextChecked) => {
                                  setAccountFilter((prev) => {
                                    const next = new Set(prev);
                                    if (nextChecked) next.add(accountName);
                                    else next.delete(accountName);
                                    return next;
                                  });
                                }}
                              />
                              <span className="text-sm truncate">{accountName}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {accountFilter.size > 0 && (
                      <div className="border-t pt-2">
                        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setAccountFilter(new Set())}>
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-[220px] h-9 text-sm justify-start">
                    <Filter className="w-4 h-4 mr-2 shrink-0" />
                    {countryFilter.size === 0 ? 'All countries' : `${countryFilter.size} countries selected`}
                    <ChevronDown className="w-4 h-4 ml-auto opacity-60 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[260px] p-2" align="end">
                  <div className="space-y-2">
                    <div className="font-semibold text-sm px-2">Filter countries</div>
                    <div className="max-h-56 overflow-y-auto">
                      {countryOptions.map((country) => {
                        const checked = countryFilter.has(country);
                        return (
                          <label key={country} className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(nextChecked) => {
                                setCountryFilter((prev) => {
                                  const next = new Set(prev);
                                  if (nextChecked) next.add(country);
                                  else next.delete(country);
                                  return next;
                                });
                              }}
                            />
                            <span className="text-sm">{country}</span>
                          </label>
                        );
                      })}
                    </div>
                    {countryFilter.size > 0 && (
                      <div className="border-t pt-2">
                        <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setCountryFilter(new Set())}>
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full sm:w-[220px] h-9 text-sm justify-start">
                    <Filter className="w-4 h-4 mr-2 shrink-0" />
                    {departmentFilter.size === 0
                      ? 'All departments'
                      : `${departmentFilter.size} departments selected`}
                    <ChevronDown className="w-4 h-4 ml-auto opacity-60 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-2" align="end">
                  <div className="space-y-2">
                    <div className="font-semibold text-sm px-2">Filter departments</div>
                    <Input
                      type="search"
                      value={departmentSearch}
                      onChange={(e) => setDepartmentSearch(e.target.value)}
                      placeholder="Search department…"
                      className="h-8 text-sm"
                      autoComplete="off"
                    />
                    <div className="max-h-56 overflow-y-auto">
                      {filteredDepartmentOptions.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                          {departmentOptions.length === 0
                            ? 'No values in personas.department for loaded rows.'
                            : 'No departments match your search.'}
                        </div>
                      ) : (
                        filteredDepartmentOptions.map((deptName) => {
                          const checked = departmentFilter.has(deptName);
                          return (
                            <label
                              key={deptName}
                              className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(nextChecked) => {
                                  setDepartmentFilter((prev) => {
                                    const next = new Set(prev);
                                    if (nextChecked) next.add(deptName);
                                    else next.delete(deptName);
                                    return next;
                                  });
                                }}
                              />
                              <span className="text-sm truncate">{deptName}</span>
                            </label>
                          );
                        })
                      )}
                    </div>
                    {departmentFilter.size > 0 && (
                      <div className="border-t pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => setDepartmentFilter(new Set())}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            </>
            )}
          </div>
        </CardContent>
      </Card>

      {cohort === 'shortlist' ? (
        <EngagementShortlistPanel
          combinedPersonas={combinedPersonas}
          personaPoolLoading={shortlistPoolLoading}
          tenantId={tenantId}
          userId={userId}
          getAccountFitScoreForRow={getAccountFitScore}
          getRowAccountTier={getRowAccountTier}
          getRowAccountSector={getRowAccountSector}
          accountTypeOptions={accountTypeOptions}
          accountIdByNameMap={accountIdByNameMap}
        />
      ) : (
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[1120px] text-sm table-fixed">
            <colgroup>
              <col style={{ width: '13%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '8%' }} />
            </colgroup>
            <thead className="bg-gray-50 text-xs text-gray-600 uppercase">
              <tr>
                <th className="px-4 py-2 text-left w-[13%]">
                  <button type="button" onClick={() => toggleSort('person_name')} className="inline-flex items-center gap-1">
                    Persona {renderSortArrow('person_name')}
                  </button>
                </th>
                <th className="px-4 py-2 text-left w-[11%]">
                  <button type="button" onClick={() => toggleSort('account_name')} className="inline-flex items-center gap-1">
                    Account {renderSortArrow('account_name')}
                  </button>
                </th>
                <th className="px-4 py-2 text-center w-[8%]">
                  <button type="button" onClick={() => toggleSort('account_fit_score')} className="inline-flex items-center justify-center gap-1 w-full">
                    Account fit {renderSortArrow('account_fit_score')}
                  </button>
                </th>
                <th className="px-4 py-2 text-center w-[9%]">
                  <button type="button" onClick={() => toggleSort('archetype_name')} className="inline-flex items-center justify-center gap-1 w-full">
                    Archetype {renderSortArrow('archetype_name')}
                  </button>
                </th>
                <th className="px-4 py-2 text-center w-[7%]">
                  <button type="button" onClick={() => toggleSort('persona_fit_score')} className="inline-flex items-center justify-center gap-1 w-full">
                    Persona fit {renderSortArrow('persona_fit_score')}
                  </button>
                </th>
                <th className="px-4 py-2 text-center w-[7%]">
                  <button type="button" onClick={() => toggleSort('touches_count')} className="inline-flex items-center justify-center gap-1 w-full">
                    Touches {renderSortArrow('touches_count')}
                  </button>
                </th>
                <th className="px-4 py-2 text-center w-[8%]">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" onClick={() => toggleSort('last_touch_at')} className="inline-flex items-center justify-center gap-1 w-full">
                          Last touch {renderSortArrow('last_touch_at')}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        Uses calendar days in your time zone (same buckets as the Last touch filter), not elapsed 24-hour periods.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
                <th className="px-4 py-2 text-center w-[7%]">
                  <button type="button" onClick={() => toggleSort('human_opens')} className="inline-flex items-center justify-center gap-1 w-full">
                    True opens {renderSortArrow('human_opens')}
                  </button>
                </th>
                <th className="px-4 py-2 text-center w-[7%]">
                  <button type="button" onClick={() => toggleSort('human_clicks')} className="inline-flex items-center justify-center gap-1 w-full">
                    True clicks {renderSortArrow('human_clicks')}
                  </button>
                </th>
                <th className="px-4 py-2 text-center w-[8%]">
                  <button type="button" onClick={() => toggleSort('last_activity_at')} className="inline-flex items-center justify-center gap-1 w-full">
                    Last activity {renderSortArrow('last_activity_at')}
                  </button>
                </th>
                <th className="px-4 py-2 text-center w-[8%]">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => toggleSort('next_scheduled')}
                          className="inline-flex items-center justify-center gap-1 w-full"
                        >
                          Next scheduled {renderSortArrow('next_scheduled')}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        The next time an auto-send message will fire for this persona — guaranteed.
                        Includes DIY auto-send, standard playbook auto-send, and scheduled ad-hoc sends.
                        Drafts awaiting your approval don&apos;t count and are excluded.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
                <th className="px-4 py-2 text-center w-[8%]">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => toggleSort('linkedin_connected')}
                          className="inline-flex items-center justify-center gap-1 whitespace-nowrap"
                        >
                          LinkedIn {renderSortArrow('linkedin_connected')}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs text-xs">
                        LinkedIn connected — true when a LinkedIn DM (not the initial connection request) has been marked sent or done for this persona.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </th>
              </tr>
            </thead>
            <tbody>
              {showUntouchedZeroRows ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-sm text-gray-500">
                    All visible personas have received at least one touch.
                  </td>
                </tr>
              ) : showEngagedZeroRows ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-sm text-gray-500">
                    No real engagement yet in this window.
                  </td>
                </tr>
              ) : showArchetypeEmptyRow ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-sm text-gray-500">
                    No personas match this archetype
                  </td>
                </tr>
              ) : showGenericFilterEmptyRow ? (
                <tr>
                  <td colSpan={12} className="px-4 py-12 text-center text-sm text-gray-500">
                    No personas match the current filters.
                  </td>
                </tr>
              ) : (
              sortedPersonas.map((p) => {
                const hasTodayEngagement =
                  isSameLocalDay(p.last_human_open_at) || isSameLocalDay(p.last_human_click_at);
                const rowClass = hasTodayEngagement
                  ? 'border-t bg-[#EAFBF5] hover:bg-[#DDF7EE]'
                  : 'border-t hover:bg-gray-50';
                const profileUrl = getPersonProfileUrl(p);
                const accountUrl = getAccountProfileUrl(p, accountIdByNameMap);
                const displayName = p.person_name || p.person_email || '—';
                const accountDisplay = p.account_name || '—';
                return (
                <tr key={engagementPersonaDedupeKey(p, 0)} className={rowClass}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-[#0A1A2F] truncate">
                      {profileUrl ? (
                        <Link
                          to={profileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0A1A2F] hover:text-[#00A3A3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
                        >
                          {displayName}
                        </Link>
                      ) : (
                        <span>{displayName}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{p.person_email}</div>
                  </td>
                  <td className="px-4 py-2 text-gray-700 truncate">
                    <div className="truncate">
                      {accountUrl ? (
                        <Link
                          to={accountUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#0A1A2F] hover:text-[#00A3A3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
                        >
                          {accountDisplay}
                        </Link>
                      ) : (
                        <span>{accountDisplay}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-center text-gray-700 whitespace-nowrap">
                    {formatAccountFitIntegerDisplay(getAccountFitScore(p))}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span
                      className="block text-gray-700 break-words leading-snug"
                      title={getPersonaArchetypeName(p) || undefined}
                    >
                      {getPersonaArchetypeName(p) || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center text-gray-700 whitespace-nowrap">
                    {p.persona_fit_score !== null && p.persona_fit_score !== undefined ? p.persona_fit_score : '—'}
                  </td>
                  <td className="px-4 py-2 text-center text-sm text-gray-700 whitespace-nowrap">
                    {(() => {
                      const toNum = (v) => {
                        const n = Number(v);
                        return Number.isFinite(n) ? n : 0;
                      };
                      const explicit =
                        p.touches_count ??
                        p.total_touches ??
                        p.total_interactions ??
                        p.interactions ??
                        null;
                      if (explicit !== null && explicit !== undefined && explicit !== '') return toNum(explicit);
                      const email = toNum(p.emails_sent);
                      const linkedin = toNum(p.linkedin_sent ?? p.linkedin_messages_sent);
                      const calls = toNum(p.call_sent ?? p.calls_sent);
                      return email + linkedin + calls;
                    })()}
                  </td>
                  <td className="px-4 py-2 text-center text-xs text-gray-600 whitespace-nowrap">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">{formatLastTouchCalendarLabel(getLastTouchAt(p))}</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {formatLastTouchTooltip(getLastTouchAt(p))}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  <td className="px-4 py-2 text-center text-sm text-gray-700 whitespace-nowrap">
                    {p.human_opens ?? 0}
                  </td>
                  <td className="px-4 py-2 text-center text-sm text-gray-700 whitespace-nowrap">
                    {p.human_clicks ?? 0}
                  </td>
                  <td className="px-4 py-2 text-center text-xs text-gray-600 whitespace-nowrap">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">{formatRel(getLastActivityAt(p))}</span>
                        </TooltipTrigger>
                        <TooltipContent>{getLastActivityAt(p) || 'never'}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  <td className="px-4 py-2 text-center text-xs text-gray-600 whitespace-nowrap">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-block">{formatNextScheduled(getNextScheduledRaw(p))}</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs">
                          {getNextScheduledRaw(p) ||
                            'No auto-send queued. May have drafts awaiting approval — check the persona\'s outbox.'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  <td className="px-4 py-2 text-center text-sm whitespace-nowrap">
                    {p.linkedin_connected === true ||
                    p.linkedin_connected === 1 ||
                    p.linkedin_connected === 'true' ? (
                      <span className="inline-flex items-center justify-center" title="LinkedIn DM sent / done">
                        <Check className="w-4 h-4 text-emerald-600" aria-label="Yes" />
                      </span>
                    ) : (
                      <span className="text-gray-400" title="No LinkedIn DM marked done">
                        N
                      </span>
                    )}
                  </td>
                </tr>
                );
              })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      )}
    </div>
  );
}
